import { RICConfiguredAddOns, RICHWElem, RICHWElemList, RICHWElemList_Str } from "@robotical/roboticaljs";
import Marty from "./Marty";
import Logger from "../../../services/logger/Logger";
import { RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR } from "@robotical/ricjs-robotical-addons/dist/RICRoboticalAddOns";

const SHOW_LOGS = true;
const TAG = "MartyAddonsManager";

export default class MartyAddonsManager {
    private _connectedAddOns: Array<RICHWElem> = new Array<RICHWElem>();


    constructor(public marty: Marty) {
        this.marty = marty;
    }

    async getHWElemList(filterByType?: string): Promise<Array<RICHWElem>> {
        // Form a list of the requests to make
        const reqList: Array<string> = [];
        if (!filterByType) {
            reqList.push("SmartServo");
            reqList.push("RSAddOn");
            reqList.push("BusPixels");
            reqList.push("!SmartServo,RSAddOn,BusPixels"); // not SmartServo or RSAddOn or BusPixels
        } else if (filterByType === "RSAddOn") {
            // we treat BusPixels as an RSAddOn
            // (batch 4 led eye add-ons have type BusPixels)
            reqList.push("RSAddOn");
            reqList.push("BusPixels");
            this._connectedAddOns = new Array<RICHWElem>();
        } else {
            reqList.push(filterByType);
        }

        // Make the requests
        const fullListOfElems = new Array<RICHWElem>();
        this._connectedAddOns = [];
        for (const reqType of reqList) {
            try {
                const hwElemList_Str = await this.marty.sendRestMessage(`hwstatus/strstat?filterByType=${reqType}`) as RICHWElemList_Str;
                // if the results of hwElem indicates that we are on an older fw version
                // send the old hwstatus command and don't expand()
                // the logic behind deciding if we are on a fw version that
                // supports strstat is: given that hwElemList_Str.hw === object[]
                // if we get back string[], then we know we are on an older version
                // if hw === empty array, then we don't have any hw elems in which
                // case we can stop at that point
                const hwElems = hwElemList_Str.hw;
                let hwElemList;
                if (hwElems.length) {
                    if (typeof hwElems[0] !== "object") {
                        // we are on an older version
                        hwElemList = await this.marty.sendRestMessage(`hwstatus?filterByType=${reqType}`) as RICHWElemList;
                    } else {
                        // we are on the fw version that supports strstat
                        hwElemList = RICHWElemList_Str.expand(hwElemList_Str);
                    }
                }
                if (hwElemList && hwElemList.rslt && hwElemList.rslt === "ok") {
                    fullListOfElems.push(...hwElemList.hw);
                    if (reqType === "RSAddOn") {
                        this._connectedAddOns = hwElemList.hw;
                        Logger.info(SHOW_LOGS, TAG,
                            `getHWElemList: found ${hwElemList.hw.length} addons/buspixels`
                        );
                    } else if (reqType === "BusPixels") {
                        // BusPixels are treated as an RSAddOn
                        this._connectedAddOns.push(...hwElemList.hw);
                        Logger.info(SHOW_LOGS, TAG,
                            `getHWElemList: found ${hwElemList.hw.length} addons/buspixels`
                        );
                    }
                }
            } catch (error) {
                Logger.info(SHOW_LOGS, TAG, `getHWElemList failed to get ${reqType} ${error}`);
                return new Array<RICHWElem>();
            }
        }
        // return the full list of elements
        return fullListOfElems;
    }

    async getAddOnConfigs(): Promise<RICConfiguredAddOns> {
        try {
            const addOnList = await this.marty.sendRestMessage("addon/list") as RICConfiguredAddOns;
            Logger.info(SHOW_LOGS, TAG, "getAddOnConfigs returned " + JSON.stringify(addOnList));
            return addOnList;
        } catch (error) {
            Logger.error(SHOW_LOGS, TAG, `getAddOnConfigs Failed to get list of add-ons ${error}`);
            return new RICConfiguredAddOns();
        }
    }

    async identifyAddOn(name: string) {
        try {
            await this.marty.sendRestMessage(`elem/${name}/json?cmd=raw&hexWr=F8`);
            return true;
        } catch (error) {
            Logger.warn(SHOW_LOGS, TAG, `identifyAddOn error ${error}`);
        }
        return false;
    }

    async setAddOnConfig(serialNo: string, newName: string) {
        try {
            await this.marty.sendRestMessage(`addon/set?SN=${serialNo}&name=${newName}`);
            return true;
        } catch (error) {
            Logger.warn(SHOW_LOGS, TAG, `setAddOnConfig error ${error}`);
        }
        return false;
    }

    async deleteAddOn(serialNo: string) {
        try {
            await this.marty.sendRestMessage(`addon/del?SN=${serialNo}`);
            return true;
        } catch (error) {
            Logger.warn(SHOW_LOGS, TAG, `deleteAddOn error ${error}`);
        }
        return false;
    }

    async getConnectedColourSensors(): Promise<string[]> {
        const names: string[] = [];
        try {
            const hwElemList_Str = await this.marty.sendRestMessage(`hwstatus/strstat/?filterByType=${RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR}`) as RICHWElemList_Str;
            const hwElems = hwElemList_Str.hw;
            let hwElemList;
            if (hwElems.length) {
                if (typeof hwElems[0] !== "object") {
                    // we are on a fw version that doesn't supports strstat
                    hwElemList = await this.marty.sendRestMessage(`hwstatus?filterByType=${RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR}`) as RICHWElemList;
                } else {
                    // we are on the fw version that supports strstat
                    hwElemList = RICHWElemList_Str.expand(hwElemList_Str);
                }
            }

            if (hwElemList?.rslt === "ok" && hwElemList.hw?.length > 0) {
                for (const hwElem of hwElemList.hw) {
                    names.push(hwElem.name);
                }
            }
            return names;
        } catch (e) {
            new Error("Error getting colour sensor names: " + JSON.stringify(e));
            return [];
        }
    }

}